<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row translation-path="sidebar.scadaPoints.scadaPointsTitle" class="pt-5 pb-3">
        <template #subtitle>
          <italic-subheader
            translation-path="sidebar.scadaPoints.scadaPointsSubtitle"
            style="font-size: 12px !important"
            class="text-left"
          />
        </template>
      </card-title-row>
      <template v-if="isFetched && mapPanelDataFetched">
        <sidebar-row class="pb-4">
          <v-row no-gutters>
            <v-col cols="7" class="text-left">
              <data-input
                clearable
                hide-details
                :placeholder="$i18n.t('default.find')"
                prepend-inner-icon="mdi-magnify"
                :data-type="{ name: 'text' }"
                v-model="filterValue"
                @input="filter"
                class="search-filter-value"
              />
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('sidebar.scadaPoints.addScadaMeasurementPoint')"
                @click="toggleNewScadaMeasurementPoint"
              />
            </v-col>
          </v-row>
        </sidebar-row>
        <template v-for="(scadaPoint, sPKey) of filteredScadaPointsRelations">
          <span :key="sPKey">
            <sidebar-row class="pb-4 pt-3">
              <v-row no-gutters align="center">
                <v-col cols="11" class="text-left text-body-2 font-weight-bold">
                  {{ scadaPoint.name }}
                </v-col>
                <v-spacer />
                <v-col cols="auto" class="pr-1">
                  <dots-menu :items="scadaPoint.dotsItems" @delete="openDeleteDialog('point', scadaPoint)" />
                </v-col>
              </v-row>
            </sidebar-row>
            <sidebar-row
              style="min-height: 40px"
              class="pb-2"
              v-for="(measure, mKey) of scadaPoint.measures"
              :key="mKey"
            >
              <v-row no-gutters align="center">
                <v-col cols="4" class="text-left text-body-2">
                  {{ measure.measurement_parameter }}
                </v-col>
                <v-spacer />
                <v-col cols="auto" class="pr-1" v-if="measure.datasource && measure.object_id">
                  <v-row no-gutters align="center">
                    <v-col cols="auto" class="text-body-2 pr-3" :style="`color: ${$_colors.primary}`">
                      {{ `${measure.datasource_name}: ${measure.object_id}` }}
                    </v-col>
                    <v-col cols="auto">
                      <v-icon size="20" @click="zoomTo(measure)" v-text="'mdi-magnify'" />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="auto" class="pr-2" v-else>
                  <data-input
                    :ref="`features-input-${scadaPoint.id}-${measure.scada_measure}`"
                    :data-type="{ name: 'features' }"
                    :disabled="
                      activeRelationButton && activeRelationButton !== `${scadaPoint.id}-${measure.scada_measure}`
                    "
                    :featuresSettings="featuresSettings"
                    :loading="
                      activeRelationButton &&
                        activeRelationButton === `${scadaPoint.id}-${measure.scada_measure}` &&
                        isAssingObjectLoading
                    "
                    @pushFeatures="assignFeatures($event, measure)"
                    @status="setActiveButton($event, `${scadaPoint.id}-${measure.scada_measure}`)"
                  />
                </v-col>
                <v-col cols="auto" class="pr-1">
                  <dots-menu
                    :items="measure.dotsItems"
                    @deleteAssignObject="openDeleteDialog('feature', measure)"
                    @deleteMeasure="openDeleteDialog('measure', measure)"
                  />
                </v-col>
              </v-row>
            </sidebar-row>
          </span>
        </template>
      </template>
      <main-loader v-else />
    </v-col>
    <main-input-dialog
      ref="addScadaMeasurementDialog"
      v-if="addScadaMeasurementLoaded"
      :is-visible.sync="addScadaMeasurementVisible"
      :inputs="addScadaMeasurementInputs"
      :loading="addScadaMeasurementLoading"
      :title-translation="$i18n.t('sidebar.scadaPoints.addScadaMeasurementPoint')"
      @agree="handleAddScadaMeasurement"
      agree-text="dialog.agreeAdd"
      dialog-width="35%"
    >
      <template v-for="input of addScadaMeasurementInputs" #[`${input.name}.input`]="data">
        <v-col :key="input.name" cols="6">
          <data-input
            v-model="newMeasurementPayload[input.name]"
            v-bind="data"
            @change="onInputValueChange(input.name, $event)"
          />
        </v-col>
      </template>
      <template #measurements.input="data">
        <v-col cols="6">
          <data-input v-model="newMeasurementPayload.measurements" v-bind="data">
            <template #selection="{ item, index }">
              <v-tooltip v-if="index === 0" bottom color="font">
                <template #activator="{ on }">
                  <v-chip v-on="on" x-small>
                    <div class="text-truncate">{{ item.verbose_name }}</div>
                  </v-chip>
                </template>
                {{ item.verbose_name }}
              </v-tooltip>
              <div v-if="index === 1" class="grey--text caption" style="white-space: nowrap !important;">
                {{ `(+${$i18n.tc('sidebar.scadaPoints.other', newMeasurementPayload.measurements.length - 1)})` }}
              </div>
            </template>
          </data-input>
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="
        $i18n.t('sidebar.scadaPoints.deleteDialogTitle', { type: $i18n.t(`sidebar.scadaPoints.${deleteType}Type`) })
      "
      :body="
        $i18n.t('sidebar.scadaPoints.deleteDialogBody', { type: $i18n.t(`sidebar.scadaPoints.${deleteType}TypeBody`) })
      "
      agree-text="dialog.agreeDelete"
      @agree="handleDelete"
    />
  </v-row>
</template>

<script>
import { debounce } from 'lodash';
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import ItalicSubheader from '@/components/ItalicSubheader';
import MainDialog from '@/components/MainDialog';
import MainInputDialog from '@/components/MainInputDialog';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
export default {
  name: 'ScadaPoints',
  components: {
    CardTitleRow,
    DataInput,
    DotsMenu,
    ItalicSubheader,
    MainDialog,
    MainInputDialog,
    MainLoader,
    SidebarRow,
  },
  computed: {
    dataSources: get('layers/metadata'),
    waterDataSourcesNamesArray() {
      return this.$_objectToArray(this.dataSources)
        .filter(dataSource => dataSource.scope === 'water')
        .map(dataSource => dataSource.name);
    },
    featuresSettings() {
      return {
        dataSources: {
          included: this.waterDataSourcesNamesArray,
        },
      };
    },
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    scadaDataSourceName: get('admin/modulesMapping@scada.datasource_name'),
    scadaPointsRelations: get('scada/scadaPointsRelations'),
    computedScadaPointsRelations() {
      return this.scadaPointsRelations.map(scadaPoint => {
        return {
          ...scadaPoint,
          dotsItems: [{ name: 'delete' }],
          measures: scadaPoint.measures.map(measure => {
            return {
              ...measure,
              dotsItems: [
                ...(measure.datasource && measure.object_id ? [{ name: 'deleteAssignObject' }] : []),
                { name: 'deleteMeasure' },
              ],
            };
          }),
        };
      });
    },
    addScadaMeasurementInputs() {
      return [
        {
          name: 'scadaPointId',
          translationPath: 'sidebar.scadaPoints.scadaMeasurementPoint',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.scadaPointsIds,
          itemText: 'desc',
          itemValue: 'id',
          disabled: !this.scadaPointsFetched,
          loading: !this.scadaPointsFetched,
        },
        {
          name: 'measurements',
          translationPath: 'default.measurement',
          cols: '12',
          rules: [v => (!!v && v.length > 0) || 'rules.required'],
          dataType: {
            name: 'select',
          },
          chips: true,
          multiple: true,
          items: this.scadaPointMeasurements,
          itemText: 'verbose_name',
          itemValue: 'id',
          disabled: !this.scadaPointsFetched || !this.scadaPointMeasurementsFetched,
          loading: this.newMeasurementPayload.scadaPointId && !this.scadaPointMeasurementsFetched,
          autoConvertSelect: false,
        },
      ];
    },
  },
  data: () => ({
    activeRelationButton: undefined,
    filterValue: '',
    addScadaMeasurementLoaded: false,
    addScadaMeasurementLoading: false,
    addScadaMeasurementVisible: false,
    deleteData: undefined,
    deleteType: '',
    filteredScadaPointsRelations: [],
    isAssingObjectLoading: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogLoading: false,
    isDeleteDialogVisible: false,
    isFetched: false,
    measurementParameters: undefined,
    newMeasurementPayload: {},
    scadaPointsFetched: false,
    scadaPointMeasurementsFetched: false,
    scadaPointsIds: [],
    scadaPointMeasurements: [],
  }),
  methods: {
    addScadaPointsRelations: call('scada/addScadaPointsRelations'),
    deleteScadaPointRelation: call('scada/deleteScadaPointRelation'),
    deleteScadaPointRelations: call('scada/deleteScadaPointRelations'),
    editScadaPointRelation: call('scada/editScadaPointRelation'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getMeasurementParameters: call('scada/getScadaMeasurementParameters'),
    getScadaPointMeasurements: call('scada/getScadaPointMeasurements'),
    getScadaPointsRelations: call('scada/getScadaPointsRelations'),
    filter(value = this.filterValue) {
      this.filteredScadaPointsRelations = this.getFilteredScadaPointsRelations(value);
    },
    getButtonStyle() {
      return this.isButtonActive
        ? {
            backgroundColor: '#E8F0FE !important',
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    getFilteredScadaPointsRelations(value) {
      return this.computedScadaPointsRelations
        .map(scadaPoint => {
          return {
            ...scadaPoint,
            measures: scadaPoint.name
              .toString()
              .toLocaleLowerCase()
              .includes((value || '').toLocaleLowerCase())
              ? scadaPoint.measures.map(measure => {
                  return {
                    ...measure,
                    measurement_parameter: this.measurementParameters[measure.measurement_parameter],
                  };
                })
              : scadaPoint.measures
                  .map(measure => {
                    return {
                      ...measure,
                      measurement_parameter: this.measurementParameters[measure.measurement_parameter],
                    };
                  })
                  .filter(measure =>
                    measure.measurement_parameter.toLocaleLowerCase().includes((value || '').toLocaleLowerCase())
                  ),
          };
        })
        .filter(scadaPoint => scadaPoint.measures?.length > 0);
    },
    openDeleteDialog(type, value) {
      this.deleteType = type;
      this.deleteData = value;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    toggleNewScadaMeasurementPoint(v = true) {
      if (v) {
        this.addScadaMeasurementLoaded = true;
        this.addScadaMeasurementVisible = true;
      } else {
        this.addScadaMeasurementVisible = false;
      }
    },
    setActiveButton(status, relation) {
      if (status && relation) {
        this.activeRelationButton = relation;
      } else {
        this.activeRelationButton = undefined;
      }
    },
    zoomTo(measure) {
      const { datasource, object_id } = measure;
      this.$root.$emit('zoomToDatasourceFeature', datasource, object_id);
    },
    async assignFeatures(features, measure) {
      this.isAssingObjectLoading = true;
      try {
        const feature = features[0];
        const data = {
          id: measure.id,
          payload: { datasource: feature.dataSource, object_id: feature.id, scada_measure: measure.scada_measure },
        };
        await this.editScadaPointRelation(data);
        this.filter();
        this.isAssingObjectLoading = false;
      } catch {
        this.$refs[`features-input-${this.activeRelationButton}`][0]?.$refs['data-input']?.toggleOff();
        this.isAssingObjectLoading = false;
      }
    },
    async getScadaPointsIds() {
      try {
        this.scadaPointsIds = await this.getDataSourceFeatures({
          dataSource: this.scadaDataSourceName,
          idsDescsOnly: true,
        });
        this.scadaPointsFetched = true;
      } catch {
        this.toggleNewScadaMeasurementPoint(false);
        this.scadaPointsFetched = false;
        this.scadaPointMeasurementsFetched = false;
      }
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        if (this.deleteType === 'point') {
          await this.deleteScadaPointRelations(this.deleteData.id);
        } else if (this.deleteType === 'measure') {
          await this.deleteScadaPointRelation(this.deleteData.id);
        } else if (this.deleteType === 'feature') {
          const data = {
            id: this.deleteData.id,
            payload: { datasource: null, object_id: null, scada_measure: this.deleteData.scada_measure },
          };
          await this.editScadaPointRelation(data);
        }
        this.filter();
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoading = false;
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    async handleAddScadaMeasurement() {
      this.addScadaMeasurementLoading = true;
      try {
        const data = this.newMeasurementPayload.measurements.map(measurement => {
          return { scada_measure: measurement, object_id: null, datasource: null };
        });
        await this.addScadaPointsRelations(data);
        this.filter();
        this.addScadaMeasurementVisible = false;
        this.addScadaMeasurementLoading = false;
        this.newMeasurementPayload = {};
      } catch {
        this.addScadaMeasurementLoading = false;
      }
    },
    async onInputValueChange(inputName, value) {
      if (inputName === 'scadaPointId') {
        this.scadaPointMeasurementsFetched = false;
        this.$refs.addScadaMeasurementDialog.$refs.form.resetValidation();
        try {
          this.newMeasurementPayload.measurements = [];
          const scadaPointMeasurements = await this.getScadaPointMeasurements(value);
          const scadaPointAdded = this.scadaPointsRelations.find(scada => scada.id == value);
          const scadaPointAddedMeasuresIds = scadaPointAdded
            ? scadaPointAdded.measures.map(measure => measure.scada_measure)
            : [];
          this.scadaPointMeasurements = scadaPointMeasurements.filter(
            measure => measure.measurement_parameter && !scadaPointAddedMeasuresIds.includes(measure.id)
          );
          this.scadaPointMeasurementsFetched = true;
        } catch {
          this.toggleNewScadaMeasurementPoint(false);
          this.newMeasurementPayload['scadaPointId'] = null;
          this.scadaPointMeasurementsFetched = false;
        }
      }
    },
    async init() {
      this.isFetched = false;
      const r = await Promise.all([this.getMeasurementParameters(), this.getScadaPointsRelations()]);
      this.measurementParameters = this.$_arrayToObject(r[0], 'name', 'verbose_name');
      this.filter();
      this.isFetched = true;
    },
  },
  watch: {
    addScadaMeasurementLoaded(nV) {
      if (nV) {
        this.getScadaPointsIds();
      }
    },
  },
  created() {
    this.filter = debounce(this.filter, 500);
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .search-filter-value {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
